import React from "react";

import { Badge } from "@/components/primitives/Badge";
import { Button } from "@/components/primitives/Button";
import { Dialog } from "@/components/primitives/Dialog";
import { Step } from "@/types";

interface RequestDetails {
  id: string;
  step_id: string;
  status?: "success" | "failure" | "unknown";
  attempt_number?: number;
  http_status: number | null;
  response_body: string | null;
  error_message: string | null;
  started_at: string;
  finished_at?: string;
  event_name?: string | null;
  event_type?: string | null;
  source_event_id?: string | null;
  source_event_index?: number | null;
  source_created_at?: string | null;
  source_updated_at?: string | null;
  metadata?: Record<string, unknown> | null;
}

interface RequestDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  run: RequestDetails | null;
  step: Step | undefined;
}

function formatJson(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function tryParseJson(value: string | null): unknown {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid min-w-0 grid-cols-1 gap-1 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
      <dt className="pt-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="min-w-0 [overflow-wrap:anywhere] text-slate-700 dark:text-slate-200">{children}</dd>
    </div>
  );
}

function CodeBlock({ value }: { value: unknown }) {
  return (
    <pre tabIndex={0} className="max-h-[320px] max-w-full overflow-auto whitespace-pre-wrap [overflow-wrap:anywhere] rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100">
      <code>{formatJson(value)}</code>
    </pre>
  );
}

export const RequestDetailsDialog: React.FC<RequestDetailsDialogProps> = ({ isOpen, onClose, run, step }) => {
  if (!run) return null;

  if (step?.step_type === "imported_event" || run.event_type) {
    const eventName = run.event_name ?? step?.name ?? "Unknown service";
    return (
      <Dialog isOpen={isOpen} onClose={onClose} title={`Event details · ${eventName.length > 80 ? eventName.slice(0, 80) + "…" : eventName}`} className="max-w-3xl" footer={<Button onClick={onClose}>Close</Button>}>
        <dl className="grid gap-4">
          <DetailRow label="Name">{eventName}</DetailRow>
          <DetailRow label="Type">{run.event_type ?? step?.event_type ?? "—"}</DetailRow>
          <DetailRow label="Result"><Badge variant={run.status === "success" ? "success" : run.status === "failure" ? "danger" : "neutral"}>{run.status ?? "unknown"}</Badge></DetailRow>
          <DetailRow label="HTTP status">{run.http_status ?? "—"}</DetailRow>
          <DetailRow label="Source ID">{run.source_event_id ?? "—"}</DetailRow>
          <DetailRow label="Source index">{run.source_event_index ?? "—"}</DetailRow>
          <DetailRow label="Created at">{run.source_created_at ? new Date(run.source_created_at).toLocaleString() : new Date(run.started_at).toLocaleString()}</DetailRow>
          <DetailRow label="Updated at">{run.source_updated_at ? new Date(run.source_updated_at).toLocaleString() : "—"}</DetailRow>
          {typeof run.metadata?.durationMs === "number" && <DetailRow label="Execution time">{run.metadata.durationMs.toFixed(2)} ms</DetailRow>}
          {run.event_type === "Cron" && (
            <>
              <DetailRow label="Run ID">{String(run.metadata?.runId ?? "—")}</DetailRow>
              <DetailRow label="Schedule">{String(run.metadata?.schedule ?? "—")} · {String(run.metadata?.timezone ?? "—")}</DetailRow>
              <DetailRow label="Source">{String(run.metadata?.executionSource ?? "—")}</DetailRow>
              <DetailRow label="Started">{String(run.metadata?.startedAt ?? "—")}</DetailRow>
              <DetailRow label="Finished">{String(run.metadata?.finishedAt ?? "—")}</DetailRow>
              {run.metadata?.result != null && <DetailRow label="Result"><CodeBlock value={run.metadata.result} /></DetailRow>}
            </>
          )}
          {run.error_message && <DetailRow label="Error">{run.error_message}</DetailRow>}
          <DetailRow label="Full metadata"><CodeBlock value={run.metadata ?? {}} /></DetailRow>
        </dl>
      </Dialog>
    );
  }

  const isDatabaseStep = step?.step_type === "database";
  const parsedResponse = tryParseJson(run.response_body);
  const isDiagnostics =
    typeof parsedResponse === "object" &&
    parsedResponse !== null &&
    "step_type" in parsedResponse &&
    parsedResponse.step_type === "database";

  const diagnostics = isDiagnostics ? (parsedResponse as Record<string, unknown>) : null;

  const requestBody = formatJson(run.step_id && step ? step.body : null);
  const responseBody = formatJson(run.response_body);
  const headers = step?.headers ? formatJson(step.headers) : "";

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Step details · ${step?.name ?? run.step_id.slice(0, 8)}`}
      className="max-w-3xl"
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-3">
          <h4 className="border-b border-slate-100 pb-2 text-base font-semibold text-slate-900 dark:border-slate-800 dark:text-white">
            {isDatabaseStep ? "Database step" : "Request"}
          </h4>
          <dl className="grid gap-3">
            {isDatabaseStep ? (
              <>
                <DetailRow label="Step type">database</DetailRow>
                <DetailRow label="DB type">
                  {String(diagnostics?.db_type ?? step?.db_config?.db_type ?? "—")}
                </DetailRow>
                <DetailRow label="Connection">
                  {diagnostics?.connection_name
                    ? String(diagnostics.connection_name)
                    : step?.db_config?.connection?.connection_id?.slice(0, 8) ?? "—"}
                </DetailRow>
                <DetailRow label="Query mode">
                  {String(diagnostics?.query_mode ?? step?.db_config?.query_mode ?? "—")}
                </DetailRow>
                {diagnostics?.executed_query && (
                  <DetailRow label="Executed query">
                    <CodeBlock value={diagnostics.executed_query} />
                  </DetailRow>
                )}
                {step?.db_config?.function_query && (
                  <DetailRow label="Function">
                    <CodeBlock value={step.db_config.function_query} />
                  </DetailRow>
                )}
                {step?.db_config?.raw_query && (
                  <DetailRow label="Raw query">
                    <CodeBlock value={step.db_config.raw_query} />
                  </DetailRow>
                )}
                {step?.db_config?.table_select_query && (
                  <DetailRow label="Table select">
                    <CodeBlock value={step.db_config.table_select_query} />
                  </DetailRow>
                )}
                {step?.db_config?.mongo_query && (
                  <DetailRow label="Mongo query">
                    <CodeBlock value={step.db_config.mongo_query} />
                  </DetailRow>
                )}
                {step?.db_config?.comparison && (
                  <DetailRow label="Comparison config">
                    <CodeBlock value={step.db_config.comparison} />
                  </DetailRow>
                )}
              </>
            ) : (
              <>
                <DetailRow label="Method">{step?.method ?? "—"}</DetailRow>
                <DetailRow label="URL">
                  <span className="font-mono">{step?.url ?? "—"}</span>
                </DetailRow>
                {step?.content_type && <DetailRow label="Content-Type">{step.content_type}</DetailRow>}
                <DetailRow label="Expected status">{step?.expected_status ?? "—"}</DetailRow>
                {headers && step && (
                  <DetailRow label="Headers">
                    <CodeBlock value={step.headers} />
                  </DetailRow>
                )}
                {requestBody && step && (
                  <DetailRow label="Body">
                    <CodeBlock value={step.body} />
                  </DetailRow>
                )}
              </>
            )}
          </dl>
        </section>

        <section className="flex flex-col gap-3">
          <h4 className="border-b border-slate-100 pb-2 text-base font-semibold text-slate-900 dark:border-slate-800 dark:text-white">
            Response
          </h4>
          <dl className="grid gap-3">
            {!isDatabaseStep && (
              <DetailRow label="Status">
                {run.http_status !== null ? (
                  <Badge variant={run.status === "success" ? "success" : "danger"}>{run.http_status}</Badge>
                ) : (
                  "—"
                )}
              </DetailRow>
            )}
            {diagnostics?.error_category != null && (
              <DetailRow label="Error category">
                <Badge variant={run.status === "success" ? "success" : "danger"}>
                  {String(diagnostics.error_category)}
                </Badge>
              </DetailRow>
            )}
            {run.status && (
              <DetailRow label="Result">
                <Badge variant={run.status === "success" ? "success" : "danger"}>{run.status}</Badge>
              </DetailRow>
            )}
            {run.attempt_number !== undefined && <DetailRow label="Attempt">{run.attempt_number}</DetailRow>}
            <DetailRow label="Started at">{new Date(run.started_at).toLocaleString()}</DetailRow>
            {run.finished_at && (
              <DetailRow label="Finished at">{new Date(run.finished_at).toLocaleString()}</DetailRow>
            )}
            {run.error_message && (
              <DetailRow label="Error">
                <pre tabIndex={0} className="max-h-[320px] max-w-full overflow-auto whitespace-pre-wrap [overflow-wrap:anywhere] rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
                  <code>{run.error_message}</code>
                </pre>
              </DetailRow>
            )}
            {responseBody && (
              <DetailRow label={isDiagnostics ? "Diagnostics" : "Response body"}>
                <CodeBlock value={run.response_body} />
              </DetailRow>
            )}
          </dl>
        </section>
      </div>
    </Dialog>
  );
};
